import { type Horizon, type HorizonKind, SCALES, contains, horizonOf, shift, today, zoomTo } from "@beresta/core";
import { Icon, IconButton, Segmented } from "@beresta/ui";
import type { ReactNode } from "react";
import { bandTitle, kickLabel, nextLabel } from "../horizonLabel.js";
import { type MessageKey, t } from "../i18n.js";

/**
 * Шапка-смуга (картки ScreenRecords, ScreenPeople, ScreenMore,
 * ScreenSettings): бурштин на всю ширину, рамка bold знизу, заокруглені
 * нижні кути. Екран у стеку має «‹ Ще» над заголовком, вкладка — ні.
 */
export interface BandHeaderProps {
  readonly title: ReactNode;
  /** `largeTitle` для кореня вкладки, `title1` для екрана в стеку. */
  readonly size?: "large" | "title";
  readonly back?: { readonly label: string; readonly onBack: () => void };
  /** Кнопки-іконки праворуч від заголовка: пошук, «+», «…». */
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}

export function BandHeader({ title, size, back, actions, children }: BandHeaderProps) {
  const large = (size ?? (back ? "title" : "large")) === "large";
  return (
    <header className="hd">
      {back && (
        <div className="hrow">
          <button type="button" className="back headline" onClick={back.onBack}>
            <Icon name="back" />
            {back.label}
          </button>
          <span className="sp" />
          {!large && actions}
        </div>
      )}
      <div className="hrow">
        <h1 className={`ttl ${large ? "large-title" : "title-1"}`}>{title}</h1>
        <span className="sp" />
        {(large || !back) && actions}
      </div>
      {children}
    </header>
  );
}

const SCALE_LABEL: Record<HorizonKind, MessageKey> = {
  year: "scale.year",
  month: "scale.month",
  week: "scale.week",
  day: "scale.day",
};

/**
 * Куди веде перемикач масштабу. Наближення бере **сьогодні**, якщо воно
 * всередині поточного проміжку, — інакше перше число: з вересня в
 * «День» людина хоче потрапити на сьогодні, а не на 1 вересня.
 */
export function zoomed(from: Horizon, kind: HorizonKind, now = today()): Horizon {
  if (kind === from.kind) return from;
  return contains(from, now) ? horizonOf(kind, now) : zoomTo(from, kind);
}

/**
 * Шапка «Часу» (картки ScaleSwitch, ScreenDay, ScreenHome): перемикач
 * чотирьох масштабів, рядок із днем тижня, «Сьогодні» й пошуком, і рядок
 * дати — «‹ 21 вересня  Завтра ›».
 *
 * Попередній період — маленький напівпрозорий кружок (ціль 44),
 * наступний — напівпрозорий напис. Між періодами рухаються й гортанням;
 * ці кнопки — для того, хто щойно відкрив день.
 */
export interface TimeHeaderProps {
  readonly horizon: Horizon;
  readonly onGo: (horizon: Horizon) => void;
  readonly onSearch: () => void;
}

export function TimeHeader({ horizon, onGo, onSearch }: TimeHeaderProps) {
  const now = today();
  const todayKey = now.toString();
  const onToday = horizon.kind === "day" && horizon.key === todayKey;
  const prev = shift(horizon, -1);
  const next = shift(horizon, 1);
  const title = bandTitle(horizon);
  // Довгі заголовки стискаються сходинками, а не переносом (картка ScreenDay).
  const titleClass =
    horizon.kind === "week" ? "ttl-wk" : title.length > 12 ? "ttl-sm" : "large-title";

  return (
    <header className="hd">
      <Segmented
        label={t("nav.scale")}
        value={horizon.kind}
        options={SCALES.map((kind) => ({ key: kind, label: t(SCALE_LABEL[kind]) }))}
        onChange={(kind) => onGo(zoomed(horizon, kind, now))}
      />
      <div className="kick">
        <span className="label">{kickLabel(horizon)}</span>
        {onToday && <span className="today label">{t("nav.today")}</span>}
        <span className="sp" />
        <IconButton icon="search" label={t("nav.search")} onClick={onSearch} />
      </div>
      <div className="prow">
        <button
          type="button"
          className="pv"
          aria-label={t(`time.prev.${horizon.kind}` as MessageKey, { label: bandTitle(prev) })}
          onClick={() => onGo(prev)}
        >
          <span className="pvr">
            <Icon name="back" size={16} />
          </span>
        </button>
        <h1 className={`ttl ${titleClass}`}>{title}</h1>
        <span className="sp" />
        <button
          type="button"
          className="nx title-3"
          aria-label={t(`time.next.${horizon.kind}.aria` as MessageKey, { label: bandTitle(next) })}
          onClick={() => onGo(next)}
        >
          {nextLabel(next, todayKey)}
          <Icon name="forward" />
        </button>
      </div>
    </header>
  );
}
